import type { BetslipOptions, ProviderCartItem } from '../providers/bettingProviderTypes'
import { DEFAULT_TRANSLATOR, type Translator } from '../i18n/translator'
import type {
  FeedItem,
  OddsMap,
  LegacyNestedSelection,
  Selection,
  SelectionAction,
  SelectionEntry,
  SelectionSet,
  ProviderExecutionRecipe,
} from '../types/feedContract'

const TOTAL_LINE_PATTERN = /\b(?:over|under)\s+([+-]?\d+(?:\.\d+)?)/i
const SIGNED_LINE_PATTERN = /(?:^|\s)([+-]\d+(?:\.\d+)?)(?:\s|$)/
const SELECTION_WORD_SEPARATOR_PATTERN = /[_\-\s]+/
const PLAYER_BY_SEPARATOR_PATTERN = /\s+by\s+/i
const HYPHEN_OR_UNDERSCORE_RUN_PATTERN = /[_-]+/
const TYPE_PREFIX_PATTERN = /^type_/

/**
 * Returns the first non-empty string among `values`. Mirrors the backend's
 * `firstString` helper and lets us read `Record<string, unknown>` maps
 * (odds / market / event / provider payloads) as display strings.
 */
function firstNonEmptyString(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value !== 'string') continue
    const trimmed = value.trim()
    if (trimmed) return trimmed
  }
  return ''
}

function firstFiniteNumber(...values: unknown[]): number | null {
  for (const value of values) {
    const numberValue = typeof value === 'number' ? value : typeof value === 'string' && value.trim() ? Number(value) : NaN
    if (Number.isFinite(numberValue)) return numberValue
  }
  return null
}

function firstIdentifier(...values: unknown[]): string {
  for (const value of values) {
    if (typeof value === 'number' && Number.isFinite(value)) return String(value)
    if (typeof value === 'string' && value.trim()) return value.trim()
  }
  return ''
}

function compactLine(value: number | null): string {
  if (value === null) return ''
  if (Object.is(value, -0) || value === 0) return '0'
  return Number.isInteger(value) ? String(value) : String(Number(value.toFixed(2)))
}

function formatLine(value: number | null, signPositive = false): string {
  const compact = compactLine(value)
  if (!compact) return ''
  return signPositive && value !== null && value > 0 ? `+${compact}` : compact
}

function lineValueFromLabel(value: unknown): number | null {
  const label = firstNonEmptyString(value)
  if (!label) return null

  const directedMatch = label.match(TOTAL_LINE_PATTERN)
  if (directedMatch) return firstFiniteNumber(directedMatch[1])

  const signedMatch = label.match(SIGNED_LINE_PATTERN)
  if (signedMatch) return firstFiniteNumber(signedMatch[1])

  return null
}

function containsText(haystack: string, needle: string): boolean {
  return haystack.toLowerCase().includes(needle.toLowerCase())
}


function lineUnitFromOccurrenceType(value: unknown): string {
  const raw = firstNonEmptyString(value)
  if (!raw || raw.toUpperCase() === 'UNTYPED') return ''
  return raw
    .split(SELECTION_WORD_SEPARATOR_PATTERN)
    .filter(Boolean)
    .map((part) => `${part.charAt(0).toUpperCase()}${part.slice(1).toLowerCase()}`)
    .join(' ')
}

function stripCountingVerbSuffix(value: string): { unit: string; stripped: boolean } {
  const trimmed = value.trim()
  const lower = trimmed.toLowerCase()
  for (const suffix of [' recorded', ' scored', ' made', ' attempted', ' completed']) {
    if (lower.endsWith(suffix)) return { unit: trimmed.slice(0, -suffix.length).trim(), stripped: true }
  }
  return { unit: trimmed, stripped: false }
}

function lineUnitFromText(value: string): string {
  const cleaned = value.split('(')[0].trim()
  if (!cleaned) return ''

  for (const segment of cleaned.split(' - ').map((part) => part.trim()).filter(Boolean)) {
    const lower = segment.toLowerCase()
    if (lower.startsWith('total ')) {
      const beforeBy = segment.split(PLAYER_BY_SEPARATOR_PATTERN)[0].trim()
      const stripped = stripCountingVerbSuffix(beforeBy)
      if (stripped.stripped) return stripped.unit
      const withoutTotal = beforeBy.slice('total '.length).trim()
      if (withoutTotal && withoutTotal.length <= 32) return withoutTotal
    }
    if (lower.startsWith('number of ')) {
      const unit = segment.slice('number of '.length).split(PLAYER_BY_SEPARATOR_PATTERN)[0].trim()
      if (unit && unit.length <= 32) return unit
    }
    if (PLAYER_BY_SEPARATOR_PATTERN.test(segment)) {
      const beforeBy = segment.split(PLAYER_BY_SEPARATOR_PATTERN)[0].trim()
      const stripped = stripCountingVerbSuffix(beforeBy)
      const unit = stripped.unit
      if (unit && unit.length <= 32) return unit
    }
    const stripped = stripCountingVerbSuffix(segment)
    if (stripped.stripped && stripped.unit && stripped.unit.length <= 32) return stripped.unit
  }

  return ''
}

function selectionLineUnit(selection: Selection | null | undefined): string {
  if (marketUsesSignedLine(selection)) return ''
  const payload = getSelectionProviderPayload(selection)
  const marketMetadata = selection?.market?.metadata
  const metadata = marketMetadata && typeof marketMetadata === 'object' && !Array.isArray(marketMetadata)
    ? (marketMetadata as Record<string, unknown>)
    : {}
  const unitFromOccurrenceType = lineUnitFromOccurrenceType(firstNonEmptyString(
    payload.criterion_occurrence_type,
    metadata.criterion_occurrence_type,
  ))
  if (unitFromOccurrenceType) return unitFromOccurrenceType
  return lineUnitFromText(firstNonEmptyString(
    selection?.subtitle,
    metadata.criterion_label,
    selection?.market?.name,
    payload.criterion_label,
    payload.market_name,
  ))
}

function kambiProviderLine(selection: Selection | null | undefined): number | null {
  const payload = getSelectionProviderPayload(selection)
  const line = firstFiniteNumber(payload.line)
  if (getSelectionProviderKey(selection) === 'kambi' && line !== null) return line / 1000
  return null
}

function appendLinePhrase(base: string, line: { display: string; unsigned: string; phrase: string; unit: string }): string {
  const addition = line.phrase || line.display
  if (!addition) return base
  if (containsText(base, addition)) return base
  if (line.unit && line.display && containsText(base, line.display)) {
    return containsText(base, line.unit) ? base : `${base} ${line.unit}`
  }
  if (line.display && containsText(base, line.display)) return base
  if (line.unsigned && line.unsigned !== line.display && containsText(base, line.unsigned)) return base
  return base ? `${base} ${addition}` : addition
}

function stripLineFromMarketLabel(market: string, line: { display: string; unsigned: string; phrase: string }): string {
  let result = market
  for (const candidate of [line.phrase, line.display, line.unsigned]) {
    if (!candidate || !result.toLowerCase().endsWith(` ${candidate.toLowerCase()}`)) continue
    result = result.slice(0, -candidate.length).trim()
  }
  return result
}

const GENERIC_MARKET_LABELS: Record<string, true> = {
  match: true,
  'match result': true,
  'match event': true,
}

const MARKET_TYPE_LABELS: Record<string, string> = {
  moneyline: 'Moneyline',
  spread: 'Spread',
  handicap: 'Handicap',
  total: 'Total',
  over_under: 'Total',
  player_prop_line: 'Player prop',
}

function marketTypeLabel(value: unknown): string {
  const raw = firstNonEmptyString(value)
  if (!raw) return ''
  const normalized = raw.toLowerCase()
  if (MARKET_TYPE_LABELS[normalized]) return MARKET_TYPE_LABELS[normalized]
  return raw
    .split(HYPHEN_OR_UNDERSCORE_RUN_PATTERN)
    .filter(Boolean)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ')
}

function preferredMarketLabel(selection: Selection | null | undefined): string {
  const payload = getSelectionProviderPayload(selection)
  const explicitMarket = firstNonEmptyString(selection?.subtitle, selection?.market?.name)
  const typeLabel = marketTypeLabel(firstNonEmptyString(selection?.market?.type, payload.market_type))
  if (explicitMarket && typeLabel && GENERIC_MARKET_LABELS[explicitMarket.toLowerCase()]) return typeLabel
  return firstNonEmptyString(explicitMarket, typeLabel, selection?.market?.type)
}

function marketUsesSignedLine(selection: Selection | null | undefined): boolean {
  const payload = getSelectionProviderPayload(selection)
  const marketText = [
    selection?.subtitle,
    selection?.market?.name,
    selection?.market?.type,
    payload.market_type,
    preferredMarketLabel(selection),
  ]
    .map((value) => firstNonEmptyString(value).toLowerCase())
    .filter(Boolean)
    .join(' ')
  return marketText.includes('spread') || marketText.includes('handicap')
}

function selectionMarketLine(selection: Selection | null | undefined): {
  display: string
  unsigned: string
  phrase: string
  unit: string
} {
  const payload = getSelectionProviderPayload(selection)
  const rawLineValue = kambiProviderLine(selection) ?? firstFiniteNumber(
    selection?.market?.points,
    payload.points,
    payload.handicap,
    lineValueFromLabel(selection?.label),
  )
  const signed = marketUsesSignedLine(selection)
  // A zero line only carries meaning for spread/handicap pick'em markets
  // ("Team A 0"). Everywhere else (yes/no, moneyline, totals, player
  // props, ...) a zero is a non-threshold and should be omitted entirely
  // rather than rendered as a literal "0".
  const isHiddenZero = signed === false && rawLineValue !== null
    && (rawLineValue === 0 || Object.is(rawLineValue, -0))
  const effectiveLineValue = isHiddenZero ? null : rawLineValue
  const display = formatLine(effectiveLineValue, signed)
  const unsigned = compactLine(effectiveLineValue)
  const unit = display ? selectionLineUnit(selection) : ''
  // Units derived from criterion text can already embed the line ("20+
  // Points" from "20+ Points Scored By The Player"). Prefixing the number
  // again would render "20 20+ Points"; the unit alone already says it all.
  const unitEmbedsLine = Boolean(unit && unsigned)
    && (unit === unsigned || unit.startsWith(`${unsigned}+`) || unit.startsWith(`${unsigned} `))
  return {
    display,
    unsigned,
    phrase: unitEmbedsLine ? unit : unit ? `${display} ${unit}` : display,
    unit,
  }
}

export function normalizeProviderKey(value: unknown): string {
  const key = String(value || '').trim().toLowerCase()
  if (key === 'og' || key === 'og.com' || key === 'ogdotcom') return 'og'
  if (key === 'crypto.com' || key === 'cryptodotcom') return 'cryptodotcom'
  if (key === 'crypto_dot_com') return 'cryptodotcom'
  return key
}

/**
 * Returns the item's `selection_set`, or synthesizes a minimal one from the
 * legacy flat `legs` list for payloads that predate the selection-set
 * contract.
 */
export function getSelectionSet(item: FeedItem | null | undefined): SelectionSet {
  if (item?.selection_set && typeof item.selection_set === 'object') return item.selection_set
  const legacySelections = Array.isArray(item?.legs) ? item.legs : []
  return {
    schema_version: '1.0',
    source: {},
    summary: {
      kind: legacySelections.length === 1 ? 'single' : legacySelections.length > 1 ? 'provider_defined' : 'none',
      label: legacySelections.length === 1 ? 'single' : legacySelections.length > 1 ? 'parlay' : 'No selections',
      selection_count: legacySelections.length,
    },
    selections: legacySelections,
    groups: [],
    entries: [],
  }
}

export function getSelections(selectionSet: SelectionSet | null | undefined): Selection[] {
  return Array.isArray(selectionSet?.selections) ? selectionSet.selections : []
}

export function getEntries(selectionSet: SelectionSet | null | undefined): SelectionEntry[] {
  return Array.isArray(selectionSet?.entries) ? selectionSet.entries : []
}

export function getPrimaryEntry(selectionSet: SelectionSet | null | undefined): SelectionEntry | null {
  return getEntries(selectionSet)[0] || null
}

export function getPrimaryAction(selectionSet: SelectionSet | null | undefined): SelectionAction | null {
  const entry = getPrimaryEntry(selectionSet)
  return entry?.action && typeof entry.action === 'object' ? entry.action : null
}

export function getExecutionRecipe(
  action: SelectionAction | null | undefined,
): ProviderExecutionRecipe | null {
  const recipe = action?.execution_recipe
  if (!recipe || recipe.recipe_version !== 1 || recipe.provider !== 'kambi' || recipe.each_way !== false) {
    return null
  }
  return recipe
}

export function executionRecipeShape(
  recipe: ProviderExecutionRecipe | null | undefined,
): 'ordinary' | 'sgp' | 'sgp_plus' | null {
  if (!recipe || !Array.isArray(recipe.rows) || recipe.rows.length === 0) return null
  const builderRows = recipe.rows.filter((row) => row.type === 'BET_BUILDER').length
  const simpleRows = recipe.rows.filter((row) => row.type === 'SIMPLE').length
  if (builderRows === 0 && simpleRows === recipe.rows.length) return 'ordinary'
  if (builderRows === 1 && simpleRows === 0) return 'sgp'
  if (builderRows === 1 && simpleRows > 0 && builderRows + simpleRows === recipe.rows.length) return 'sgp_plus'
  return null
}

export function selectionEntryIsExpired(
  entry: SelectionEntry | null | undefined,
  now = Date.now(),
): boolean {
  if (!entry?.valid_until) return false
  const validUntil = Date.parse(entry.valid_until)
  return !Number.isFinite(validUntil) || validUntil <= now
}

/** A Kambi system type ("trixie", "super heinz"), or '' for a plain system bet. */
function systemBetName(entry: SelectionEntry): string {
  const rawLabel = firstNonEmptyString(entry.label, entry.coupon_type)
    .toLowerCase()
    .replace(TYPE_PREFIX_PATTERN, '')
    .replaceAll('_', ' ')
    .replace('superheinz', 'super heinz')
  return rawLabel && rawLabel !== 'system' ? rawLabel : ''
}

type EntryBetType =
  | { kind: 'none' }
  | { kind: 'single' }
  | { kind: 'parlay' }
  | { kind: 'system'; name: string }

/**
 * What kind of wager a selection set is, before it is put into words: the
 * English label (`selectionSetBetTypeLabel`) and the widget's translated one
 * (`selectionSetBetTypeDisplayLabel`) read the same answer.
 */
type BetType =
  | EntryBetType
  | { kind: 'repeated'; entry: EntryBetType; count: number }
  | { kind: 'wagers'; count: number }

function entryBetType(entry: SelectionEntry | null | undefined): EntryBetType {
  switch (entry?.kind) {
    case 'single':
      return { kind: 'single' }
    case 'parlay':
    case 'same_game_parlay':
      return { kind: 'parlay' }
    case 'system':
      return { kind: 'system', name: systemBetName(entry) }
    default:
      return { kind: 'none' }
  }
}

function sameBetType(left: EntryBetType, right: EntryBetType): boolean {
  return left.kind === right.kind
    && (left.kind !== 'system' || right.kind !== 'system' || left.name === right.name)
}

function entryOutcomeCount(entry: SelectionEntry): number {
  const action = entry.action && typeof entry.action === 'object' ? entry.action : null
  const outcomeIDs = Array.isArray(action?.outcome_ids) ? action.outcome_ids : []
  if (outcomeIDs.length > 0) return outcomeIDs.length
  return Array.isArray(entry.selection_ids) ? entry.selection_ids.length : 0
}

export function selectionSetIsSameGameParlay(selectionSet: SelectionSet | null | undefined): boolean {
  return selectionSet?.summary?.kind === 'same_game_parlay'
    || getEntries(selectionSet).some((entry) => entry.kind === 'same_game_parlay')
}

/**
 * True when two or more selections sit on the same event (an explicit
 * same-game group, a same_game_parlay entry, or legs sharing an event id).
 * The provider prices correlated legs itself, so multiplying their
 * individual odds fabricates a combined price; callers must skip
 * client-side multiplication for these sets and wait for a live quote.
 */
export function selectionSetHasCorrelatedLegs(selectionSet: SelectionSet | null | undefined): boolean {
  if (selectionSetIsSameGameParlay(selectionSet)) return true
  const groups = Array.isArray(selectionSet?.groups) ? selectionSet.groups : []
  if (groups.some((group) => group.kind === 'same_game' && group.selection_ids.length > 1)) return true
  const seen = new Set<string>()
  for (const selection of getSelections(selectionSet)) {
    const key = firstIdentifier(selection.provider_ref?.event_id, selection.event?.id, selection.event?.name)
    if (!key) continue
    if (seen.has(key)) return true
    seen.add(key)
  }
  return false
}

export function selectionSetHasCombinedOdds(selectionSet: SelectionSet | null | undefined): boolean {
  const entries = getEntries(selectionSet)
  if (selectionSet?.schema_version !== '2.0') return entries.length <= 1
  if (entries.length !== 1) return false
  const entry = entries[0]
  if (entry.kind === 'single') return entryOutcomeCount(entry) <= 1
  if (['parlay', 'same_game_parlay'].includes(entry.kind)) return true

  // Provider-normalized multi-event parlays deliberately retain a
  // provider_defined/mixed entry kind because their copy semantics are not a
  // canonical Kambi "combination". They are still one aggregate wager, so
  // their authored entry/summary odds are valid combined odds. Multiple
  // entries remain excluded above, preserving the distinction from separate
  // singles and system combinations.
  return ['provider_defined', 'mixed'].includes(entry.kind)
    && entryOutcomeCount(entry) > 1
}

function hasOddsValue(odds: OddsMap | null | undefined): odds is OddsMap {
  if (!odds || typeof odds !== 'object') return false
  if (firstNonEmptyString(odds.american, odds.us)) return true
  const decimal = firstFiniteNumber(odds.decimal, odds.dec)
  return decimal !== null && decimal > 1
}

export function selectionSetCombinedOdds(
  selectionSet: SelectionSet | null | undefined,
): OddsMap | null {
  if (!selectionSetHasCombinedOdds(selectionSet)) return null
  if (hasOddsValue(selectionSet?.summary?.odds)) return selectionSet.summary.odds
  const entryOdds = getPrimaryEntry(selectionSet)?.odds
  return hasOddsValue(entryOdds) ? entryOdds : null
}

export function selectionSetIsMultipleSingles(selectionSet: SelectionSet | null | undefined): boolean {
  if (selectionSet?.schema_version !== '2.0') return false
  const entries = getEntries(selectionSet)
  if (entries.length === 0 || entries.some((entry) => entry.kind !== 'single')) return false
  const singleCount = entries.reduce((total, entry) => total + Math.max(entryOutcomeCount(entry), 1), 0)
  return singleCount > 1
}

function selectionSetBetType(selectionSet: SelectionSet | null | undefined): BetType {
  const selections = getSelections(selectionSet)
  if (selections.length === 0) return { kind: 'none' }

  const entries = getEntries(selectionSet)
  if (selectionSet?.schema_version === '2.0' && entries.length > 0) {
    if (entries.every((entry) => entry.kind === 'single')) {
      const singleCount = entries.reduce((total, entry) => total + Math.max(entryOutcomeCount(entry), 1), 0)
      return singleCount === 1 ? { kind: 'single' } : { kind: 'repeated', entry: { kind: 'single' }, count: singleCount }
    }
    const types = entries.map(entryBetType)
    const first = types[0]
    if (types.length > 1 && types.every((type) => sameBetType(type, first))) {
      return { kind: 'repeated', entry: first, count: types.length }
    }
    if (types.length > 1) return { kind: 'wagers', count: types.length }
    if (first.kind !== 'none') return first
  }

  const primaryEntry = entries[0]
  const action = primaryEntry?.action && typeof primaryEntry.action === 'object' ? primaryEntry.action : null
  if (action?.coupon_type === 'single') return { kind: 'single' }
  if (action?.coupon_type === 'combination') return { kind: 'parlay' }
  if (action?.coupon_type === 'system') return { kind: 'system', name: '' }

  const kind = selectionSet?.summary?.kind || primaryEntry?.kind
  if (kind === 'single') return { kind: 'single' }
  if (kind === 'same_game_parlay' || kind === 'parlay') return { kind: 'parlay' }
  return selections.length === 1 ? { kind: 'single' } : { kind: 'parlay' }
}

type BetTypeTranslate = Translator['t']

/** Puts a bet type into words with the given message lookup. */
function betTypeLabel(type: BetType, t: BetTypeTranslate): string {
  switch (type.kind) {
    case 'none':
      return ''
    case 'single':
      return t('bet.single')
    case 'parlay':
      return t('bet.parlay')
    case 'system':
      return type.name || t('bet.systemBet')
    case 'wagers':
      return t('bet.wagers', { count: type.count })
    case 'repeated':
      if (type.entry.kind === 'single') return t('bet.singles', { count: type.count })
      if (type.entry.kind === 'parlay') return t('bet.parlays', { count: type.count })
      return t('bet.typedBets', { count: type.count, label: betTypeLabel(type.entry, t) })
  }
}

/**
 * The selection set's wager type in English: "single", "parlay", "3 singles",
 * "2 trixie bets". Logic compares against these; screens show
 * `selectionSetBetTypeDisplayLabel`.
 */
export function selectionSetBetTypeLabel(selectionSet: SelectionSet | null | undefined): string {
  return betTypeLabel(selectionSetBetType(selectionSet), DEFAULT_TRANSLATOR.t)
}

/** `selectionSetBetTypeLabel` in the widget's chrome language. */
export function selectionSetBetTypeDisplayLabel(
  selectionSet: SelectionSet | null | undefined,
  t: BetTypeTranslate,
): string {
  return betTypeLabel(selectionSetBetType(selectionSet), t)
}

export function getSelectionProviderKey(selection: Selection | null | undefined): string {
  return normalizeProviderKey(
    selection?.provider_ref?.payload?.provider ||
      selection?.provider_ref?.key ||
      selection?.provider?.payload?.provider ||
      selection?.provider?.key,
  )
}

export function getSelectionProviderPayload(selection: Selection | null | undefined): Record<string, unknown> {
  const payload = selection?.provider_ref?.payload || selection?.provider?.payload
  return payload && typeof payload === 'object' ? payload : {}
}

export function getActionProviderKey(selectionSet: SelectionSet | null | undefined): string {
  const action = getPrimaryAction(selectionSet)
  return normalizeProviderKey(action?.provider_key || selectionSet?.source?.provider_key)
}

export function getOutcomeIdsFromAction(action: SelectionAction | null | undefined): number[] {
  return Array.isArray(action?.outcome_ids)
    ? action.outcome_ids.map(Number).filter((value) => Number.isFinite(value) && value > 0)
    : []
}

export function getActionOutcomeIds(selectionSet: SelectionSet | null | undefined): number[] {
  return getOutcomeIdsFromAction(getPrimaryAction(selectionSet))
}

function isProviderCartItem(item: Record<string, unknown> | null | undefined): item is ProviderCartItem {
  return Boolean(item && typeof item === 'object' && item.provider)
}

export function getProviderCartItemsFromAction(action: SelectionAction | null | undefined): ProviderCartItem[] {
  return Array.isArray(action?.items) ? action.items.filter(isProviderCartItem) : []
}

export function getActionProviderCartItems(selectionSet: SelectionSet | null | undefined): ProviderCartItem[] {
  return getProviderCartItemsFromAction(getPrimaryAction(selectionSet))
}

export function getTargetURLFromAction(action: SelectionAction | null | undefined): string {
  if (typeof action?.target_url === 'string' && action.target_url.trim()) return action.target_url
  const items = getProviderCartItemsFromAction(action)
  return String(items.find((item) => item.detail_url)?.detail_url || '')
}

export function getActionTargetURL(selectionSet: SelectionSet | null | undefined): string {
  return getTargetURLFromAction(getPrimaryAction(selectionSet))
}

export function normalizeCouponType(
  action: SelectionAction | null | undefined,
  outcomeCount: number,
): BetslipOptions['couponType'] | null {
  const rawType = String(action?.coupon_type || '').trim().toLowerCase().replace(TYPE_PREFIX_PATTERN, '').replace('super_heinz', 'superheinz')
  switch (rawType) {
    case 'single':
    case 'combination':
    case 'system':
    case 'patent':
    case 'trixie':
    case 'yankee':
    case 'canadian':
    case 'heinz':
    case 'superheinz':
      return rawType
    case '':
    case 'provider_defined':
      return outcomeCount <= 1 ? 'single' : 'combination'
    default:
      return null
  }
}

export function getAuthoredActionStakes(
  entry: SelectionEntry | null | undefined,
  action: SelectionAction | null | undefined,
  couponType: BetslipOptions['couponType'],
  outcomeCount: number,
): number[] | undefined {
  const payloadStakes = action?.payload?.stakes
  const rawStakes = Array.isArray(entry?.stakes)
    ? entry.stakes
    : Array.isArray(payloadStakes)
      ? payloadStakes
      : []
  const stakes = rawStakes.filter(
    (value): value is number => typeof value === 'number' && Number.isSafeInteger(value) && value > 0,
  )
  const expectedCount = couponType === 'single' ? outcomeCount : 1
  return stakes.length === rawStakes.length && stakes.length === expectedCount ? stakes : undefined
}

export function selectionOdds(selection: Selection | null | undefined): OddsMap | string | number {
  return selection?.odds || {}
}

export function selectionDisplayName(selection: Selection | null | undefined): string {
  const baseName = firstNonEmptyString(selection?.label, selection?.selection?.name, selection?.name)
  const line = selectionMarketLine(selection)
  return appendLinePhrase(
    baseName || firstNonEmptyString(selection?.market?.name, selection?.market?.type),
    line,
  )
}

export function selectionMarketLabel(selection: Selection | null | undefined): string {
  const market = preferredMarketLabel(selection)
  const line = selectionMarketLine(selection)
  if (!market) return line.phrase
  return stripLineFromMarketLabel(market, line)
}

export function selectionSubtitle(selection: Selection | null | undefined): string {
  return selectionMarketLabel(selection)
}

export function selectionEventName(selection: Selection | null | undefined): string {
  return firstNonEmptyString(selection?.event?.name)
}

export function providerSelectionId(selection: Selection | null | undefined): string {
  return String(selection?.provider_ref?.selection_id || selection?.provider?.selection_id || '')
}

export interface SelectionPresentation {
  selection: string
  market: string
  event: string
  odds: OddsMap | string | number
  outcomeId: string
  betOfferId: string
}

export function providerOutcomeId(selection: Selection | null | undefined): string {
  const payload = getSelectionProviderPayload(selection)
  const nestedSelection = selection?.selection as (LegacyNestedSelection & { outcome_id?: string | number }) | undefined
  return firstIdentifier(
    payload.outcome_id,
    nestedSelection?.outcome_id,
    selection?.provider_ref?.selection_id,
    selection?.provider?.selection_id,
  )
}

export function providerBetOfferId(selection: Selection | null | undefined): string {
  const payload = getSelectionProviderPayload(selection)
  const marketMetadata = selection?.market?.metadata
  const metadata = marketMetadata && typeof marketMetadata === 'object' && !Array.isArray(marketMetadata)
    ? (marketMetadata as Record<string, unknown>)
    : {}
  return firstIdentifier(
    payload.bet_offer_id,
    payload.betOfferId,
    metadata.bet_offer_id,
    selection?.provider_ref?.market_id,
  )
}

export function selectionPresentation(selection: Selection | null | undefined): SelectionPresentation {
  return {
    selection: selectionDisplayName(selection),
    market: selectionMarketLabel(selection),
    event: selectionEventName(selection),
    odds: selectionOdds(selection),
    outcomeId: providerOutcomeId(selection),
    betOfferId: providerBetOfferId(selection),
  }
}
